/**
 * Squeeze Momentum (SQZ)
 *
 * Detects when Bollinger Bands are inside Keltner Channels (squeeze).
 * Momentum histogram shows direction of the breakout.
 *
 * Category: Advanced | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA, computeEMA } from "./sma";
import { computeATR } from "./atr";

export const squeezeIndicator: IndicatorDefinition = {
  id: "squeeze",
  name: "Squeeze Momentum",
  shortName: "SQZ",
  category: "advanced",
  pane: "separate",
  params: [
    { key: "bbPeriod", label: "BB Period", type: "number", default: 20, min: 2, max: 100 },
    { key: "bbMult", label: "BB Mult", type: "number", default: 2, min: 0.5, max: 5, step: 0.1 },
    { key: "kcPeriod", label: "KC Period", type: "number", default: 20, min: 2, max: 100 },
    { key: "kcMult", label: "KC Mult", type: "number", default: 1.5, min: 0.5, max: 5, step: 0.1 },
  ],
  plots: [
    { key: "momentum", label: "Momentum", color: "#2196F3", style: "histogram", lineWidth: 1, priceScaleId: "sqz", histogramBase: 0 },
    { key: "squeeze", label: "Squeeze", color: "#FF9800", style: "circles", lineWidth: 3, priceScaleId: "sqz" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { bbPeriod, bbMult, kcPeriod, kcMult } = params;
    const closes = candles.map(c => c.close);
    const bbSma = computeSMA(closes, bbPeriod);
    const kcEma = computeEMA(closes, kcPeriod);
    const atr = computeATR(candles, kcPeriod);

    const momData: { time: number; value: number; color?: string }[] = [];
    const sqzData: { time: number; value: number; color?: string }[] = [];

    for (let i = Math.max(bbPeriod, kcPeriod) - 1; i < candles.length; i++) {
      if (isNaN(bbSma[i]) || isNaN(kcEma[i]) || isNaN(atr[i])) continue;

      // BB bands
      let sqSum = 0;
      for (let j = i - bbPeriod + 1; j <= i; j++) sqSum += (closes[j] - bbSma[i]) ** 2;
      const std = Math.sqrt(sqSum / bbPeriod);
      const bbUpper = bbSma[i] + bbMult * std;
      const bbLower = bbSma[i] - bbMult * std;

      // KC channels
      const kcUpper = kcEma[i] + kcMult * atr[i];
      const kcLower = kcEma[i] - kcMult * atr[i];

      // Squeeze on/off
      const sqzOn = bbLower > kcLower && bbUpper < kcUpper;

      // Momentum: linear regression of (close - avg(highest_high, lowest_low, sma))
      const lookback = bbPeriod;
      if (i >= lookback - 1) {
        let hh = -Infinity, ll = Infinity;
        for (let j = i - lookback + 1; j <= i; j++) {
          if (candles[j].high > hh) hh = candles[j].high;
          if (candles[j].low < ll) ll = candles[j].low;
        }
        const mid = (hh + ll) / 2;
        const val = candles[i].close - (mid + bbSma[i]) / 2;
        const prevVal = momData.length > 0 ? momData[momData.length - 1].value : 0;

        momData.push({
          time: candles[i].time,
          value: val,
          color: val > 0 ? (val > prevVal ? "#26A69A" : "#B2DFDB") : (val < prevVal ? "#EF5350" : "#FFCDD2"),
        });

        sqzData.push({
          time: candles[i].time,
          value: 0,
          color: sqzOn ? "#F44336" : "#4CAF50",
        });
      }
    }

    return [{ key: "momentum", data: momData }, { key: "squeeze", data: sqzData }];
  },
};
